import React, { useContext, useRef, } from "react";
// import { handClickEvent } from "@/common/google_analytics"; // Google Analyticsのための関数
import { MoneyLinkWarning } from "@/shared/utils/custom-exception";
import { utilities } from "@/shared/utils/utility";
import { useValidators } from "@/shared/utils/validate";
import {
  ErrorDialogContext,
  errorHandledAsync,
  errorHandledSync,
} from "@/shared/context/error-context";
import { LoginService } from "@/features/auth/services/login-service";
import { LoadingModalContext } from "@/shared/components/loading/loading-context";
import { useLoginContext } from "@/features/auth/contexts/login-context";
import { useLoginStates } from "@/features/auth/hooks/use-login-states";
import { CodeModalResult } from "@/features/two-step-authentication/hooks/use-two-step-authentication-code";
//import { useTwoStepAuthCodeStates } from "@/features/two-step-authentication/hooks/use-two-step-authentication-code-states";
import { TwoStepAuthService } from "@/features/two-step-authentication/services/two-step-authentication-service";
import { EndpointPathNames } from "@/shared/config/endpoint-path";
import { useModalContext } from "@/shared/components/modal/contexts/modal-context";
import { useNavigate } from "react-router-dom";

export const useLogin = () => {
  const {
    setEmail,
    additionalParams,
    setSoriId,
  } = useLoginContext();
  const {
    setOpenNotLoginModal,
  } = useModalContext();
  const {
    password,
    setPassword,
    errorMessage,
    setErrorMessage,
    isActiveLoginButton,
    setIsActiveLoginButton,
  } = useLoginStates();

  const codeModalResolveRef = useRef<((result: CodeModalResult) => void) | null>(null);

  // グローバルエラーハンドリング
  const { setError } = useContext(ErrorDialogContext);

  // 共通関数の呼び出し
  const { validatesForm } = utilities();
  const { looseMailAddressValidator } = useValidators();
  const navigate = useNavigate();

  // ローディング用コンテキスト
  const loadingCtx = useContext(LoadingModalContext);

  const loginService = new LoginService();
  const twoStepAuthService = new TwoStepAuthService();

  // メールアドレス
  const onChangeMailAddress = (value: string) => {
    setEmail(value);
  };

  // パスワード
  const onChangePassword = (value: string) => {
    setPassword(value);
  };

  // ログインボタン押下
  const onClickLogin = async (
    email: string,
    password: string,
    e: React.MouseEvent,
  ) => {
    // handClickEvent({ event: "click", label: "click_login_login" });
    loadingCtx.setIsLoading(true);
    try {
      e.preventDefault();

      if (!validatesForm([looseMailAddressValidator], email, setErrorMessage)) {
        setErrorMessage("利用者IDはメールアドレス形式である必要があります。");
        return;
      }

      if (password.length < 8) {
        setErrorMessage("パスワードが短いです。8文字以上で入力してください。");
        return;
      }
      if (password.length > 15) {
        setErrorMessage("パスワードが長いです。15文字以下で入力してください。");
        return;
      }

      const res = await loginService.postLogin({
        owner_id: email,
        owner_pw: password,
      });

      // コンバート未完了時はダイアログで表示
      if (res.convert_status === 0) {
        if (res.sori_id) {
          setSoriId(res.sori_id);
        }
        setOpenNotLoginModal("NotConverted");
        return;
      }
      else if (res.convert_status === 1) {
        setError(new MoneyLinkWarning({
          title: "ログインできません",
          message: (
            <>
              現在「MoneyLink」Web版へデータ引継ぎ中です。<br/>
              完了までお待ちください。
            </>
          ),
        }));
        return;
      }

      // 仮登録の場合は本登録処理へ飛ばす
      if (res.registration_status === 0) {
        navigate(EndpointPathNames.FIRST_AUTH);
        return;
      }

      // 2段階認証
      if (res.is_2sa === true) {
        // モーダル表示前にローディングを解除
        loadingCtx.setIsLoading(false);
        // モーダルの結果が返ってくるまで待つ
        const twoStepAuthResult = await returnCodeModalResult();

        if (twoStepAuthResult.status === "ok") {
          setOpenNotLoginModal(undefined);
        } else if (twoStepAuthResult.status === "failed") {
          // 失敗時の処理
          return;
        } else {
          // キャンセル時・その他の処理
          setOpenNotLoginModal(undefined);
          return;
        }
      }

      window.location.replace(EndpointPathNames.HOME + additionalParams);
    } catch (error) {
      console.log(error);
      // APIエラーは再throw してerrorHandledAsyncで処理
      throw error;
    } finally {
      loadingCtx.setIsLoading(false);
    }
  };

  // 2段階認証の確認
  const returnCodeModalResult = () => {
    setOpenNotLoginModal("TwoStepAuthCode");
    // 認証コード発行
    twoStepAuthService.generateTwoStepAuthCode({
      purpose: "login",
    });
    return new Promise<CodeModalResult>((resolve) => {
      codeModalResolveRef.current = resolve;
    });
  };

  // 2段階認証入力モーダルを閉じる時に呼ぶコールバック
  const callbackCodeModal = (result: CodeModalResult) => {
    setOpenNotLoginModal(undefined);
    codeModalResolveRef.current?.(result);
    codeModalResolveRef.current = null;
  };

  // ログイン情報が更新された場合のコールバック関数定義
  const changeLoginButtonState = (email: string, password: string) => {
    setIsActiveLoginButton(email !== "" && password !== "");
  };

  return {
    password,
    setPassword,
    errorMessage,
    setErrorMessage,
    isActiveLoginButton,
    onChangeMailAddress: errorHandledSync(onChangeMailAddress, setError),
    onChangePassword: errorHandledSync(onChangePassword, setError),
    onClickLogin: errorHandledAsync(onClickLogin, setError),
    changeLoginButtonState: errorHandledSync(changeLoginButtonState, setError),
    callbackCodeModal: errorHandledSync(callbackCodeModal, setError),
  };
};
